/**
 * Le chiamate del caricamento, e quella che dice chi sta lavorando.
 *
 * La lettura della cartella e' una chiamata a parte da quella che apre il
 * caricamento: guardare una cartella sbagliata tre volte non deve lasciare tre
 * caricamenti aperti a meta', e la risposta dev'essere leggibile — «camera 2
 * senza scatti» — prima che parta un byte.
 */
import { ask, get, send } from './api'
import type { Declared } from './folder'

export interface Camera {
  label: string
  folder: string
  frames: number
  alignments: boolean
}

/** Che cosa il servizio ha capito della cartella. */
export interface Recognised {
  name: string
  survey_type: string | null
  epsg: number | null
  cameras: Camera[]
  clouds: number
  trajectory: boolean
  swim_input: boolean
  unknown_datapacks: string[]
  files: number
  bytes: number
  problems: string[]
  warnings: string[]
}

export interface Missing {
  path: string
  url: string
}

export interface UploadState {
  upload_id: string
  state: string
  objects_done: number
  objects_total: number
  bytes_done: number
  bytes_total: number
  missing: Missing[]
}

/** Legge la cartella senza aprire niente. */
export function inspect(files: readonly Declared[], manifest: unknown): Promise<Recognised> {
  // Non porta chiave di idempotenza: non scrive, e una chiave darebbe la
  // stessa risposta a una cartella cambiata nel frattempo.
  return ask<Recognised>('/uploads/inspect', { files, manifest })
}

/** Che cosa manca ancora: e' questa la ripresa.
 *
 * Cio' che la pagina credeva di aver mandato non conta; conta cio' che
 * l'archivio ha. Una contabilita' tenuta nel navigatore direbbe «fatto» anche
 * per un invio troncato dalla chiusura della scheda. */
export function state(uploadId: string): Promise<UploadState> {
  return get<UploadState>(`/uploads/${uploadId}`)
}

export interface Closed {
  upload_id: string
  job_id: string
  state: string
  objects: number
  receipt_id: string
}

/** Chiude la sessione: da qui nasce l'acquisizione, e va da sola in coda. */
export function close(uploadId: string, key: string): Promise<Closed> {
  return send<Closed>(`/uploads/${uploadId}/close`, {}, key)
}

/** Apre il caricamento e riceve dove depositare. */
export function open(
  files: readonly Declared[],
  manifest: unknown,
  key: string,
): Promise<UploadState> {
  return send<UploadState>('/uploads', { files, manifest }, key)
}

/** Un caricamento aperto, come lo vede chi lo ritrova. */
export interface Interrupted {
  upload_id: string
  package_name: string | null
  objects_done: number
  objects_total: number
  bytes_done: number
  bytes_total: number
  created_at: string
  opened_by: string | null
}

/** I caricamenti ancora aperti, per riprenderli da qualunque macchina. */
export function interrupted(): Promise<Interrupted[]> {
  return get<Interrupted[]>('/uploads')
}
